/// <amd-module name="Environments/Providers/Kubernetes/YamlReader" />
import * as Resources from "Environments/Providers/Kubernetes/Resources";
import * as React from "react";
import { IVssComponentContext, VssComponent, WrappedComponent } from "VSS/Platform/Layout";
import { IVssThemeService } from "VSS/Platform/Theming";
import { CardContent, CustomCard } from "VSSUI/Card";
import { Spinner, SpinnerSize } from "VSSUI/Spinner";
import { css } from "VSSUI/Util";
import { IEditorProps } from "VSS/Features/Monaco/Contracts";

export interface IYamlReaderProperties {
    contentProps?: any;
    contentClassName?: string;
    className?: string;
    contentLanguage: string;
    onEditorCreated: (editor: any) => void;
}

interface YamlReaderState {
    monacoTheme: any /* monaco.editor.BuiltinTheme */;
}

export class YamlReader extends VssComponent<IYamlReaderProperties, YamlReaderState>{
    constructor(props: IYamlReaderProperties, context: IVssComponentContext) {
        super(props, context);
        this.state = { monacoTheme: this._getDefaultTheme() };
    }

    public render(): React.ReactNode {
        return this._getYamlReader();
    }

    private _getYamlReader(): React.ReactNode {
        const { contentLanguage, className, contentClassName, contentProps: props } = this.props;
        const theme = props && props.options && props.options.theme || this.state.monacoTheme;
        const content = props.text || "";
        const componentLoading: () => JSX.Element = () => <Spinner size={SpinnerSize.large} label={Resources.LoadingLegacyComponent} />;
        /* monaco.editor.IEditorOptions */
        const options = {
            readOnly: true,
            renderWhitespace: "all",
            fontSize: 13,
            lineHeight: 20,
            minimap: { enabled: false },
            scrollbar: { horizontalScrollbarSize: 16 },
            lineNumbers: "on",
            extraEditorClassName: "k8s-monaco-editor",
            theme: theme,
            language: contentLanguage,
            ...props.options,
            value: content
        };

        return (
            <CustomCard className={css(className || "", "monaco-editor", "k8s-card-padding", "flex-grow")}>
                <CardContent className={css(contentClassName || "", "k8s-reader-content")} contentPadding={false}>
                    <div className="k8s-reader-outer">
                        <div className={"flex-row absolute-fill justify-center"}>
                            <WrappedComponent<IEditorProps>
                                dependencies={["ms.vss-features.editor"]}
                                wrappedType="code-editor"
                                wrappedProps={{
                                    className: "k8s-ui-host-devops",
                                    editorOptions: options,
                                    monacoTheme: theme,
                                    content: {
                                        content: content,
                                        contentType: contentLanguage
                                    },
                                    editorRef: this._onEditorCreated,
                                    lazyVisualContentChange: true
                                }}
                                loadingComponent={componentLoading}
                            />
                        </div>
                    </div>
                </CardContent>
            </CustomCard>
        );
    }

    public componentDidMount(): void {
        this._themeService.subscribe(this._onThemeChanged, "themeChanged")
    }

    public componentWillUnmount(): void {
        this._themeService.unsubscribe(this._onThemeChanged, "themeChanged")
    }

    private _onThemeChanged = () => {
        this.setState({ monacoTheme: this._getDefaultTheme() });
    }

    private _getDefaultTheme(): any /* monaco.editor.BuiltinTheme */ {
        const currentTheme = this._themeService.getCurrentTheme();
        return currentTheme && currentTheme.isDark ? "vs-dark" : "vs";
    }

    private _onEditorCreated = (editor: any) => {
        if (this.props.onEditorCreated) {
            this.props.onEditorCreated(editor);
        }
    }

    private get _themeService() {
        return this.context.pageContext.getService<IVssThemeService>("IVssThemeService");
    }
}
